import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FileText, ShieldCheck, ChevronRight } from "lucide-react";
import { useLang } from "../context/LangContext";
import { api, mediaUrl } from "../lib/api";
import { Seo } from "../components/Seo";

const numberClauses = (clauses = [], chapterNum = null) => {
  const counters = [];
  let lastLevel = 1;
  return clauses.map((c) => {
    if ((c.type || "text") !== "text") return { ...c, num: null, indentLevel: lastLevel };
    let lv = Math.max(1, c.level || 1);
    if (lv > counters.length + 1) lv = counters.length + 1;
    counters.length = lv;
    counters[lv - 1] = (counters[lv - 1] || 0) + 1;
    lastLevel = lv;
    const base = counters.slice(0, lv).join(".");
    return { ...c, num: chapterNum ? `${chapterNum}.${base}` : base, indentLevel: lv };
  });
};

export default function LegalPage() {
  const { slug } = useParams();
  const { t, tr, lang } = useLang();
  const [menu, setMenu] = useState([]);
  const [doc, setDoc] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => { api.get("/public/legal/menu").then((r) => setMenu(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setDoc(null); setNotFound(false);
    const target = slug || "area legal";
    api.get(`/public/legal/documents/${encodeURIComponent(target)}`).then((r) => setDoc(r.data)).catch(() => setNotFound(!!slug));
  }, [slug]);

  const prevent = (e) => e.preventDefault();

  return (
    <div data-testid="legal-page">
      <Seo page="legal" robotsOverride="noindex,follow" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-1 lg:grid-cols-4 gap-10">
        <aside className="lg:col-span-1" data-testid="legal-sidebar">
          <div className="lg:sticky lg:top-24 space-y-8">
            <div>
              <span className="font-mono-tech text-xs tracking-[0.3em] text-[#5698C7]">ÁREA LEGAL</span>
              <nav className="mt-4">
                <Link to="/legal" className={`flex items-center gap-2 text-sm py-2 ${!slug ? "text-[#5698C7]" : "text-slate-600 hover:text-[#0B1220]"} transition-colors duration-200`} data-testid="legal-menu-index">
                  <ShieldCheck size={14} strokeWidth={1.5} /> {t("nav_legal")}
                </Link>
              </nav>
            </div>
            {menu.map((cat) => (
              <div key={cat._id} data-testid={`legal-category-${cat._id}`}>
                <div className="font-mono-tech text-[10px] tracking-[0.25em] text-slate-500 mb-3 border-b border-slate-200 pb-2">{tr(cat.name).toUpperCase()}</div>
                <nav className="space-y-1">
                  {(cat.documents || []).filter((d) => d.slug !== "area legal").map((d) => (
                    <Link key={d.slug} to={`/legal/${d.slug}`} data-testid={`legal-menu-${d.slug}`}
                      className={`flex items-center gap-2 text-sm py-1.5 transition-colors duration-200 ${slug === d.slug ? "text-[#5698C7]" : "text-slate-600 hover:text-[#0B1220]"}`}>
                      <ChevronRight size={12} className={slug === d.slug ? "text-[#5698C7]" : "text-slate-500"} /> {tr(d.title)}
                    </Link>
                  ))}
                </nav>
              </div>
            ))}
          </div>
        </aside>

        <main className="lg:col-span-3 min-h-[50vh]">
          {!slug && !doc && !notFound && (
            <div className="hud-border p-10" data-testid="legal-index">
              <FileText size={28} strokeWidth={1} className="text-[#5698C7] mb-6" />
              <h1 className="font-display text-2xl sm:text-3xl font-light mb-4">{t("legal_teaser_title")}</h1>
              <p className="text-sm text-slate-600 max-w-2xl leading-relaxed">{t("legal_teaser_sub")}</p>
              <p className="text-sm text-slate-500 mt-4">{t("legal_select")}</p>
            </div>
          )}
          {notFound && <div className="hud-border p-10 text-sm text-slate-600" data-testid="legal-not-found">404 — Documento no encontrado.</div>}
          {doc && (
            <article onCopy={prevent} onContextMenu={prevent} className="legal-protected" data-testid="legal-document">
              <div className="hud-border bg-white p-6 mb-8 space-y-1" data-testid="legal-doc-metadata">
                <div className="font-mono-tech text-[10px] tracking-widest text-slate-500 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                  <span>{t("legal_version").toUpperCase()}: <span className="text-[#5698C7]">v{doc.version}</span></span>
                  <span>{t("legal_author").toUpperCase()}: <span className="text-[#0B1220]">{doc.author}</span></span>
                  <span>{t("legal_published").toUpperCase()}: <span className="text-[#0B1220]">{doc.published_at?.slice(0, 10)}</span></span>
                  <span>{t("legal_updated").toUpperCase()}: <span className="text-[#0B1220]">{doc.updated_at?.slice(0, 10)}</span></span>
                </div>
                <div className="font-mono-tech text-[10px] tracking-widest text-slate-500 pt-2 break-all">
                  {t("legal_hash").toUpperCase()}: SHA-256 {doc.hash}
                </div>
                {doc.original_hash && (
                  <div className="font-mono-tech text-[10px] tracking-widest text-slate-500 pt-1 break-all border-t border-slate-100 mt-2" data-testid="legal-original-hash">
                    {(doc.original_label || "Versión 1 · Primera versión · 24 de marzo de 2016 · Original").toUpperCase()} — SHA-256 {doc.original_hash}
                  </div>
                )}
              </div>
              <h1 className={`font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-[#14324F] ${doc.slug === "area legal" ? "mb-3" : "mb-8"}`} data-testid="legal-doc-title">{tr(doc.title)}</h1>
              {doc.slug === "area legal" && (
                <p className="text-sm text-slate-500 mb-8" data-testid="legal-doc-subtitle">{t("legal_teaser_sub")}</p>
              )}
              {doc.mode === "clauses" && ((doc.chapters || []).length > 0 || (doc.preamble || []).length > 0) ? (
                <div className="legal-content text-sm" data-testid="legal-doc-clauses">
                  {(doc.preamble || []).map((b, i) => (
                    <p key={b.id || i} className={/^\s*Versi[oó]n/i.test(b.text?.es || "") ? "version-note" : ""}
                      dangerouslySetInnerHTML={{ __html: b.text?.[lang] || b.text?.es || "" }} />
                  ))}
                  {(doc.chapters || []).map((ch, ci) => (
                    <section key={ch.id || ci} className="mt-8" data-testid={`legal-chapter-${ci}`}>
                      <h2 className="font-display text-lg font-bold text-[#14324F] mb-4">{ci + 1}. {tr(ch.title)}</h2>
                      <div className="space-y-3">
                        {numberClauses(ch.clauses, ci + 1).map((c, i) => {
                          const indent = { marginLeft: `${Math.min((c.indentLevel || 1) - 1, 6) * 1.4}rem` };
                          const txt = c.text?.[lang] || c.text?.es || "";
                          const type = c.type || "text";
                          if (type === "list") return (
                            <ul key={c.id || i} className="clause-list" style={indent}>
                              {txt.split("\n").filter(Boolean).map((it, li) => <li key={li} dangerouslySetInnerHTML={{ __html: it }} />)}
                            </ul>
                          );
                          if (type === "table") return (
                            <div key={c.id || i} className="overflow-x-auto" style={indent}>
                              <table className="legal-table"><tbody>
                                {(c.rows || []).map((row, ri) => (
                                  <tr key={ri}>{row.map((cell, cli) => <td key={cli} dangerouslySetInnerHTML={{ __html: cell?.[lang] || cell?.es || "" }} />)}</tr>
                                ))}
                              </tbody></table>
                            </div>
                          );
                          if (type === "image") return (
                            <figure key={c.id || i} className="legal-figure" style={indent}>
                              {c.image?.url && <img src={mediaUrl(c.image.url)} alt={c.image?.caption?.[lang] || c.image?.caption?.es || ""} loading="lazy" />}
                              <figcaption>
                                {[c.image?.code, c.image?.caption?.[lang] || c.image?.caption?.es, c.image?.copyright].filter(Boolean).join(" · ")}
                              </figcaption>
                            </figure>
                          );
                          if (type === "note") return <p key={c.id || i} style={indent} dangerouslySetInnerHTML={{ __html: txt }} />;
                          return (
                            <p key={c.id || i} className="clause-p" style={indent}>
                              <span className="clause-num">{c.num}.</span>
                              <span dangerouslySetInnerHTML={{ __html: txt }} />
                            </p>
                          );
                        })}
                      </div>
                    </section>
                  ))}
                </div>
              ) : (
                <div className="legal-content text-sm" data-testid="legal-doc-content"
                  dangerouslySetInnerHTML={{ __html: doc.content?.[lang] || doc.content?.es || "" }} />
              )}
              <div className="mt-12 border-t border-slate-200 pt-6">
                <p className="font-mono-tech text-[10px] tracking-widest text-slate-500" data-testid="legal-copyright">{t("legal_copyright")}</p>
              </div>
            </article>
          )}
        </main>
      </div>
    </div>
  );
}
